import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "./Reveal";
import { LOCATION, IMAGES, ADDRESS, MAPS_URL } from "../../lib/content";
import { MapPin, Navigation } from "lucide-react";

export default function Location() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section
      id="location"
      data-testid="location"
      ref={ref}
      className="relative bg-[#050505] py-28 md:py-40 overflow-hidden scroll-mt-20"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Panorama */}
          <div className="relative order-2 lg:order-1">
            <div className="gold-border p-3 overflow-hidden">
              <div className="overflow-hidden">
                <motion.img
                  style={{ y }}
                  src={IMAGES.hills}
                  alt="Layered Himalayan foothills around Kasauli"
                  className="w-full h-[380px] md:h-[520px] object-cover scale-125 will-change-transform"
                />
              </div>
            </div>
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute top-6 left-6 glass border border-[#d4af37]/20 px-5 py-3 hover:border-[#d4af37]/60 transition-colors duration-300"
            >
              <p className="text-[10px] tracking-luxe uppercase text-[#d4af37]">
                Subathu Road · Dharampur
              </p>
              <p className="font-serif-lux text-lg text-[#f5f5f0]">
                30.90°N · 76.96°E
              </p>
            </a>
          </div>

          {/* List */}
          <div className="order-1 lg:order-2">
            <Reveal>
              <span className="text-xs tracking-luxe uppercase text-[#d4af37]">
                The Location
              </span>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif-lux text-4xl md:text-6xl mt-6 leading-[1.05] text-[#f5f5f0]">
                Remote enough to breathe.
                <span className="italic gold-gradient-text">
                  {" "}
                  Close enough to matter.
                </span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-[#9a968c] leading-relaxed max-w-lg">
                Tucked into the cedar slopes above Kasauli, yet effortlessly
                connected to the plains — the perfect distance between the world
                you left and the one you're building.
              </p>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="mt-8 gold-border glass p-6 flex flex-col sm:flex-row sm:items-center gap-5 justify-between">
                <div className="flex items-start gap-3">
                  <MapPin size={18} strokeWidth={1.5} className="text-[#d4af37] mt-0.5" />
                  <div>
                    <p className="text-[10px] tracking-luxe uppercase text-[#d4af37]">
                      Site Address
                    </p>
                    <p className="font-serif-lux text-xl text-[#f5f5f0] mt-1">
                      {ADDRESS}
                    </p>
                  </div>
                </div>
                <a
                  data-testid="location-directions"
                  href={MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold rounded-full px-6 py-3 text-xs tracking-[0.16em] uppercase font-semibold flex items-center gap-2 whitespace-nowrap shrink-0"
                >
                  <Navigation size={14} /> Get Directions
                </a>
              </div>
            </Reveal>

            <div className="mt-10 space-y-1">
              {LOCATION.map((l, i) => (
                <Reveal key={l.place} delay={0.05 * i}>
                  <div className="group flex items-center justify-between border-t border-[#d4af37]/12 py-5">
                    <div className="flex items-center gap-4">
                      <MapPin
                        size={16}
                        strokeWidth={1.5}
                        className="text-[#d4af37]"
                      />
                      <span className="text-[#f5f5f0] text-lg">{l.place}</span>
                    </div>
                    <span className="text-sm text-[#9a968c] tracking-wide">
                      {l.meta}
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        {/* Live interactive map */}
        <Reveal delay={0.1}>
          <div className="mt-16 relative gold-border p-3">
            <div className="relative overflow-hidden">
              <iframe
                title="Sarrgosa — Subathu Road, Dharampur / Kasauli location map"
                data-testid="location-map"
                src="https://www.google.com/maps?q=Subathu%20Road%2C%20Dharampur%2C%20Kasauli%2C%20Himachal%20Pradesh&z=13&output=embed"
                className="w-full h-[380px] md:h-[460px] grayscale-[0.2] contrast-[1.05]"
                style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <a
              data-testid="location-map-directions"
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-6 right-6 btn-gold rounded-full px-6 py-3 text-xs tracking-[0.16em] uppercase font-semibold flex items-center gap-2 shadow-lg"
            >
              <Navigation size={14} /> Open in Google Maps
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
